import { tintRgbTohex } from "components/ColorItem/ColorItem"
import queryClient from "providers/queryClient"
import { clearBillingState } from "store/billing/billingSlice"
import { clearStoredCheckoutSessionId } from "services/billingCheckoutStorage"
import { clearProductSlice } from "store/product/productSlice"
import { store } from "store/store"
import { clearUserInfo } from "store/user/userSlice"
import {
  type Cover,
  type GlassesDraftListItem,
  type GlassesEditorDto,
  type GlassesEditorInitialSku,
  type GlassesLimits,
  type MerchantId,
  type ProductV2,
} from "types"
import { convertGlassesZipToObject } from "utils/zipEffectFileReader"
import { adminApi } from "./adminApi"
import { uploadPlatformAsset } from "./assetUpload"
import { getClerkAppsyncToken } from "./platformApiAuth"
import { type ProductType } from "store/product/types"
import { type ProductCategories } from "components/Navbar/types"

type ClerkWindow = Window & {
  Clerk?: {
    signOut?: (options?: { redirectUrl?: string }) => Promise<void>
  }
}

type ProductTypesQueryResult = {
  data?: {
    types?: ProductType[]
  }
}

type ProductByIdSku = {
  externalId?: string | null
  /** Per-variant try-on visibility (variant display.hidden). */
  hidden?: boolean | null
  id: string
  name: string
  sku: string
  upc: string
  url: string
  vto: {
    color: string
    coverage: string
    effectUrl?: string
    finish: string
    region: string
  }
}

type ProductByIdResult = {
  productV2: {
    brand: string
    brandSlug: string
    category: {
      id: number
    }
    cover: string
    externalId?: string | null
    id: string
    merchant?: string
    name: string
    skus: ProductByIdSku[]
    type: string
    visible?: boolean
  }
}

// Platform-only service: every call goes to platform_api (admin REST + the
// scoped asset pipeline). The legacy GraphQL/AppSync transport was removed
// together with the legacy backend decoupling.
const normalizeSkuSegment = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

/**
 * The SKU saveProductV2 actually sends for a variant: the merchant's own value
 * when present, otherwise a deterministic slug (the variant card has no SKU
 * input, so added variants always arrive empty). Exported so post-save
 * verification can compare the server state against the real request payload —
 * comparing the form's empty SKU with the stored slug misfires.
 */
export const effectiveVariantSku = (
  productItem: Pick<ProductV2, "externalId" | "id" | "name">,
  color: Pick<ProductV2["colors"][number], "color" | "externalId" | "name" | "sku">,
  index: number
) => {
  const ownSku = color.sku?.trim()
  if (ownSku) return ownSku

  const parts = [
    productItem.externalId,
    productItem.id,
    productItem.name,
    color.externalId,
    color.name,
    color.color,
    String(index + 1),
  ]
    .map((part) => normalizeSkuSegment(part ?? ""))
    .filter(Boolean)

  return parts.length ? parts.join("-").slice(0, 128) : `makeup-${index + 1}`
}

export class TintVTOService {
  async getClerkAppsyncToken() {
    return getClerkAppsyncToken()
  }

  async getCurrentAdmin() {
    return adminApi.getCurrentAdmin({ suppressPaywall: true })
  }

  logout() {
    const loginRedirectUrl = `${window.location.origin}/login`
    clearStoredCheckoutSessionId()
    store.dispatch(clearBillingState())
    store.dispatch(clearUserInfo())
    store.dispatch(clearProductSlice())
    queryClient.clear()

    const clerk = (window as ClerkWindow).Clerk
    if (clerk?.signOut) {
      void clerk.signOut({ redirectUrl: loginRedirectUrl })
      return
    }

    window.location.href = loginRedirectUrl
  }

  async types(): Promise<ProductTypesQueryResult> {
    const response = await adminApi.listProductTypes()

    return {
      data: {
        types: response.items,
      },
    }
  }

  async fetchAllProducts(merchantId: MerchantId, category: ProductCategories) {
    const allProducts: ProductType[] = []

    const { data } = await this.productsV2(0, "", category, merchantId)

    const firstPageItems = (data?.productsV2?.items || []) as ProductType[]
    allProducts.push(...firstPageItems)

    const totalPages = data?.productsV2?.pageInfo?.pageQty || 1

    if (totalPages <= 1) return allProducts

    const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 1)
    const pageRequests = remainingPages.map((page) =>
      this.productsV2(page, "", category, merchantId)
    )

    const responses = await Promise.all(pageRequests)

    responses.forEach(({ data }) => {
      const items = (data?.productsV2?.items || []) as ProductType[]
      allProducts.push(...items)
    })

    return allProducts
  }

  async productsV2(
    page: number,
    partial: string,
    category: string,
    merchant: string,
    limit?: number
  ) {
    const response = await adminApi.listProducts({
      category,
      ...(limit ? { limit } : {}),
      merchantId: merchant,
      page,
      partial,
    })

    return {
      data: {
        productsV2: {
          items: response.items,
          pageInfo: {
            pageQty: response.pageCount,
          },
        },
      },
    }
  }

  async productByIdV2(productId: string): Promise<ProductV2> {
    const data: ProductByIdResult = {
      productV2: (await adminApi.getProduct(productId)) as ProductByIdResult["productV2"],
    }

    let coverLeft: Cover | null = null,
      coverFront: Cover | null = null,
      coverRight: Cover | null = null

    if (data.productV2?.skus[0]?.vto.effectUrl) {
      const res = await convertGlassesZipToObject(data.productV2?.skus[0]?.vto.effectUrl)
      coverLeft = res.coverLeft ?? null
      coverFront = res.coverFront ?? null
      coverRight = res.coverRight ?? null
    }

    return {
      upc: "",
      id: data.productV2.id,
      name: data.productV2.name,
      cover: data.productV2.cover,
      externalId: data.productV2.externalId ?? undefined,
      brand: data.productV2.brand ?? "",
      brandSlug: data.productV2.brandSlug,
      type: data.productV2.type,
      categoryId: data.productV2.category.id,
      visible: data.productV2.visible ?? false,
      sku: data.productV2.skus[0].sku,
      region: data.productV2.skus[0].vto.region,
      coverage: data.productV2.skus[0].vto.coverage,
      finish: data.productV2.skus[0].vto.finish,
      url: data.productV2.skus[0].url,
      effectUrl: data.productV2.skus[0].vto.effectUrl,
      coverLeft,
      coverFront,
      coverRight,
      colors: data.productV2.skus.map((sku) => {
        return {
          color: sku.vto.color[0] !== "#" ? tintRgbTohex(sku.vto.color) : sku.vto.color,
          upc: sku.upc,
          sku: sku.sku,
          skuId: sku.id,
          externalId: sku.externalId ?? undefined,
          name: sku.name,
          hidden: sku.hidden ?? false,
        }
      }),
      merchant: data.productV2.merchant ?? "",
    }
  }

  async getGlassesEditor(productId: string): Promise<GlassesEditorDto> {
    return await adminApi.getGlassesEditor(productId)
  }

  async createGlassesDraft(payload: {
    cover: string
    externalId?: string | null
    initialSkus?: GlassesEditorInitialSku[]
    name: string
    url?: string | null
  }): Promise<GlassesEditorDto> {
    return await adminApi.createGlassesDraft(payload)
  }

  async getGlassesDraft(draftId: string): Promise<GlassesEditorDto> {
    return await adminApi.getGlassesDraft(draftId)
  }

  async listGlassesDrafts(params: {
    merchantId: string
    partial?: string
  }): Promise<GlassesDraftListItem[]> {
    return await adminApi.listGlassesDrafts(params)
  }

  async getGlassesLimits(params: { merchantId: string }): Promise<GlassesLimits> {
    return await adminApi.getGlassesLimits(params)
  }

  async saveGlassesDraft(
    draftId: string,
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      url?: string | null
    }
  ): Promise<GlassesEditorDto> {
    return await adminApi.saveGlassesDraft(draftId, payload)
  }

  async deleteGlassesDraft(draftId: string): Promise<{ success: true }> {
    return await adminApi.deleteGlassesDraft(draftId)
  }

  async finalizeGlassesDraft(
    draftId: string,
    payload: {
      cover: string
      effectUrl?: string | null
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      url?: string | null
      visible: boolean
    }
  ): Promise<GlassesEditorDto> {
    return await adminApi.finalizeGlassesDraft(draftId, payload)
  }

  async saveGlassesEditor(
    productId: string,
    payload: {
      cover: string
      effectUrl?: string | null
      externalId?: string | null
      name: string
      url?: string | null
      visible: boolean
    }
  ): Promise<GlassesEditorDto> {
    return await adminApi.saveGlassesEditor(productId, payload)
  }

  async createGlassesSourceImageUploadUrl(payload: { contentType: string }) {
    return await adminApi.createGlassesSourceImageUploadUrl(payload)
  }

  async startGlassesDraftDigitization(
    draftId: string,
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      sourceImageKeys: string[]
      url?: string | null
    }
  ) {
    return await adminApi.startGlassesDraftDigitization(draftId, payload)
  }

  async startInitialGlassesDraftDigitization(payload: {
    cover: string
    externalId?: string | null
    initialSkus?: GlassesEditorInitialSku[]
    name: string
    sourceImageKeys: string[]
    url?: string | null
  }) {
    return await adminApi.startInitialGlassesDraftDigitization(payload)
  }

  async startGlassesDigitization(payload: {
    cover: string
    externalId?: string | null
    initialSkus?: GlassesEditorInitialSku[]
    name: string
    productId?: string | null
    sourceImageKeys: string[]
    url?: string | null
  }) {
    return await adminApi.startGlassesDigitization(payload)
  }

  async deleteProduct(productId: string) {
    const response = await adminApi.deleteProduct(productId)
    return {
      data: {
        deleteProduct: response.success,
      },
    }
  }

  async saveProductV2(productItem: ProductV2) {
    const productId = productItem.id
    const categoryId = Number(productItem.categoryId)
    const typeId = Number(productItem.typeId)

    let cover = productItem.cover

    if (productItem.file) {
      const uploaded = await uploadPlatformAsset({
        file: productItem.file,
        fileName: productItem.file.name,
        merchantId: productItem.merchant,
        purpose: "image",
      })
      cover = uploaded.publicUrl
    }

    let skus
    if (productItem.colors.length > 0)
      skus = productItem.colors.map(
        (
          objColor: {
            color: string
            upc: string
            sku: string
            skuId?: string
            externalId?: string
            name: string
            hidden?: boolean
          },
          index: number
        ) => ({
          id: objColor.skuId ?? "",
          externalId: objColor.externalId?.trim() || null,
          hidden: objColor.hidden ?? false,
          sku: effectiveVariantSku(productItem, objColor, index),
          upc: objColor.upc,
          name: objColor.name,
          productId: productItem.id,
          url: productItem.url?.trim() || null,
          vto: {
            color: objColor.color.replaceAll(",", "").replaceAll(" ", ""),
            coverage: productItem.coverage,
            finish: productItem.finish,
            region: productItem.region,
          },
        })
      )
    else
      skus = [
        {
          id: "",
          sku: "",
          upc: "",
          name: "",
          productId: productItem.id,
          url: productItem.url?.trim() || null,
          vto: {
            color: "",
            coverage: productItem.coverage,
            finish: productItem.finish,
            region: productItem.region,
          },
        },
      ]

    const variables = {
      externalId: productItem.externalId?.trim() || null,
      name: productItem.name,
      merchant: productItem.merchant,
      id: productId,
      brand: productItem.brand,
      categoryId,
      typeId,
      cover,
      price: {
        amount: 0,
        currency: "USD",
      },
      skus,
    }

    const response = await adminApi.saveProduct(variables)
    return {
      data: {
        saveProductV2: response.id,
      },
    }
  }

  async toggleProductVisibility(productId: string, visible: boolean) {
    const response = await adminApi.toggleProductVisibility(productId, visible)
    return {
      data: {
        toggleProductVisibility: response.success,
      },
    }
  }

  // A logo field is a base64 data url (newly picked), "" (cleared), or undefined
  // (unchanged). Upload a fresh pick through the scoped asset pipeline and return
  // its asset id; the caller maps "" to a remove flag and undefined to a no-op.
  private async uploadLogoAssetId(dataUrl: string, merchantId: string): Promise<string> {
    const blob = await (await fetch(dataUrl)).blob()
    const uploaded = await uploadPlatformAsset({
      file: blob,
      merchantId,
      purpose: "image",
    })
    return uploaded.assetId
  }

  async updateUICustomization({
    merchantId,
    data,
    mainLogo,
    shortLogo,
  }: {
    merchantId: string
    data: Record<string, string>
    mainLogo?: string
    shortLogo?: string
  }) {
    const response = await adminApi.saveUiCustomization({
      data: JSON.stringify(data),
      merchantId,
      ...(mainLogo
        ? { mainLogoAssetId: await this.uploadLogoAssetId(mainLogo, merchantId) }
        : mainLogo === ""
        ? { removeMainLogo: true }
        : {}),
      ...(shortLogo
        ? { shortLogoAssetId: await this.uploadLogoAssetId(shortLogo, merchantId) }
        : shortLogo === ""
        ? { removeShortLogo: true }
        : {}),
    })
    return {
      data: {
        saveStyles: response.success,
      },
    }
  }

  async getUICustomization(merchantId: string) {
    const response = await adminApi.getUiCustomization(merchantId)
    return {
      data: {
        getStyles: response.styles,
      },
    }
  }

  async getProductTranslations(productId: string, skuIds: string[]) {
    return await adminApi.getProductTranslations(productId, skuIds)
  }

  async saveProductTranslation(
    productId: string,
    merchantId: string,
    translations: Array<{ code: string; name: string; description?: string }>
  ) {
    const response = await adminApi.saveProductTranslations(productId, {
      data: JSON.stringify(translations),
      merchantId,
    })
    return {
      data: {
        saveI8N: response.success,
      },
    }
  }

  async saveSKUTranslation(
    skuId: string,
    merchantId: string,
    translations: Array<{ code: string; name: string; description?: string }>,
    productId?: string
  ) {
    const scopedProductId = productId?.trim()
    if (!scopedProductId) throw new Error("productId is required to save SKU translations")

    const response = await adminApi.saveSkuTranslations(skuId, {
      data: JSON.stringify(translations),
      merchantId,
      productId: scopedProductId,
    })
    return {
      data: {
        saveI8N: response.success,
      },
    }
  }
}

const tintVTOServiceCommon = new TintVTOService()
export const getTintVTOService = () => tintVTOServiceCommon

export const tintVTOService = getTintVTOService()
